import React from "react";
import TrackingUtils from "@tracking/utils/TrackingUtils";
import TrackingSuspense from "@tracking/core/TrackingSuspense/TrackingSuspense";
import BrandTitle from "@tracking/core/BrandTitle";
import PageTitle from "@tracking/core/PageTitle";
import { renderRoutes } from "react-router-config";
import {
  Container,
  SideBar,
  Content,
  Header,
  HeaderItemTitle,
  Body,
  ItemList,
  IconItemList,
  ListCollapsed,
  useStyles,
} from "./styles";
import { NavLink, useLocation } from "react-router-dom";
import ExportCSVComponent from "@tracking/core/ExportCSVComponent";
import NotificationSystem from "./NotificationSystem";
import { Collapse, List, ListItemText, Icon } from "@material-ui/core";
import { useAppDispatch, useAppSelector } from "app/store";
import { openTrackingDialog } from "app/store/trackingApp/trackingDialogSlice";
import ConfirmDialog from "@tracking/core/ConfirmDialog";
import { PublicClientApplication } from "@azure/msal-browser";
import { loginRequest, msalConfig } from "app/auth/useAuth";
import {
  selectAuthHomeAccountID,
  selectStateAuth,
} from "app/auth/store/auth.slice";
import {
  selectUserConfigRoutes_MenuItems,
  selectUserConfigRoutes_RenderItems,
} from "app/user-setup/store/userSetup.selectors";
import {
  selectMenuIsOpen,
  setMenuOpen,
} from "app/store/trackingApp/menuLayoutSlice";

const msalInstance = new PublicClientApplication(msalConfig);

interface ItemProps {
  id: string;
  path: string;
  title: string;
  icon: any;
  type: string;
  children: any;
  exact: boolean;
  menuIsCollapsed: boolean;
  setOpenMenu: any;
}

const logoutAction = async (homeAccountId: string) => {
  const currentAccount: any = msalInstance.getAccountByHomeId(homeAccountId);
  const logoutHint = currentAccount.idTokenClaims.login_hint;
  await msalInstance.logoutRedirect({
    ...loginRequest,
    logoutHint: logoutHint,
    account: currentAccount,
    authority: "https://login.microsoftonline.com/common",
  });
};

const ItemMenu: React.FC<ItemProps> = (props) => {
  const {
    id,
    path,
    title,
    icon,
    type,
    children,
    exact,
    menuIsCollapsed,
    setOpenMenu,
  } = props;
  const location = useLocation();
  const [open, setOpen] = React.useState(() =>
    TrackingUtils.needsToBeOpened(location, props)
  );
  const [wasOpenBeforeCollapse, setWasOpenBeforeCollapse] =
    React.useState(false);
  const dispatch = useAppDispatch();
  const homeAccountId: string = useAppSelector(selectAuthHomeAccountID);

  React.useEffect(() => {
    if (!menuIsCollapsed && open) {
      setWasOpenBeforeCollapse(true);
      setOpen(false);
    } else if (menuIsCollapsed && wasOpenBeforeCollapse && !open) {
      setOpen(true);
      setWasOpenBeforeCollapse(false);
    }
  }, [menuIsCollapsed, open, wasOpenBeforeCollapse]);

  const handleClick = () => {
    setOpen(!open);
    if (open) {
      setOpenMenu(false);
    } else if (!open) {
      setOpenMenu(true);
    }
  };

  const handleLogout = () => {
    dispatch(
      openTrackingDialog({
        children: (
          <ConfirmDialog
            action={() => logoutAction(homeAccountId)}
            title="Confirmar saída"
            text="Tem certeza que deseja sair do CB Tracking?"
          />
        ),
      })
    );
  };

  const handleClickItemNav = () => {
    if (id === "logout") {
      handleLogout();
    }
  };

  return (
    <>
      {type === "item" ? (
        <ItemList
          button
          selected={location.pathname === path}
          onClick={handleClickItemNav}
        >
          <NavLink
            className="flex items-center gap-1 font-medium flex-1"
            exact={exact}
            to={path ?? "#"}
          >
            <IconItemList>
              <Icon>{icon}</Icon>
            </IconItemList>
            <ListItemText className="whitespace-nowrap" primary={title} />
          </NavLink>
        </ItemList>
      ) : (
        <>
          <ItemList button onClick={handleClick}>
            <NavLink
              className="flex items-center gap-1 font-medium flex-1"
              to={"#"}
            >
              <IconItemList>
                <Icon>{icon}</Icon>
              </IconItemList>
              <ListItemText className="whitespace-nowrap" primary={title} />
              {menuIsCollapsed && (
                <Icon>{open ? "expand_less" : "expand_more"}</Icon>
              )}
            </NavLink>
          </ItemList>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <ListCollapsed>
              {children.map((child, index) => (
                <ItemList
                  button
                  key={index}
                  selected={location.pathname === child.rota}
                  className="collapsedItem"
                >
                  <NavLink
                    className="flex items-center gap-1 font-medium flex-1 collapsedItemLink"
                    exact
                    to={child.rota}
                  >
                    <IconItemList>
                      <Icon>{child.icone}</Icon>
                    </IconItemList>
                    <ListItemText
                      className="whitespace-nowrap"
                      primary={child.descricao}
                    />
                  </NavLink>
                </ItemList>
              ))}
            </ListCollapsed>
          </Collapse>
        </>
      )}
    </>
  );
};

const Layout1: React.FC = () => {
  const location = useLocation();
  const USER = useAppSelector(selectStateAuth);
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const openMenu = useAppSelector(selectMenuIsOpen);

  React.useEffect(() => {
    dispatch(
      setMenuOpen(
        location.pathname === "/torre-pesados" ||
          location.pathname === "/lista-pedidos" ||
          location.pathname === "/lista-pedidos-lml" ||
          location.pathname === "/lista-pedidos-minihub" ||
          location.pathname === "/torre-lml" ||
          location.pathname === "/torre-minihub" ||
          location.pathname === "/torre-fracionados"
          ? false
          : true
      )
    );
  }, [location.pathname, dispatch]);

  const handleSetOpenMenu = (value: boolean) => {
    dispatch(setMenuOpen(value));
  };

  const routesUserMenu = useAppSelector(selectUserConfigRoutes_MenuItems);
  const routesUserRender = useAppSelector(selectUserConfigRoutes_RenderItems);

  return (
    <Container>
      <SideBar
        className={`${classes.transitionMenu} ${!openMenu ? "w-16" : "w-72"}`}
      >
        <BrandTitle setOpenMenu={handleSetOpenMenu} openMenu={openMenu} />
        <List
          component="nav"
          aria-labelledby="nested-list-subheader"
          className={`${!openMenu ? "" : classes.menuNav} ${
            classes.sideBarNav
          } overflow-x-auto`}
        >
          {routesUserMenu.map((route: any) => {
            if (!route.icone) return "";
            return (
              <ItemMenu
                id={route.idMenu}
                path={route.rota}
                title={!openMenu ? "" : route.descricao}
                icon={route.icone}
                type={route.tipo}
                exact={true}
                children={route.subMenus ? route.subMenus : []}
                key={route.idMenu}
                menuIsCollapsed={openMenu}
                setOpenMenu={handleSetOpenMenu}
              />
            );
          })}
        </List>
      </SideBar>
      <Content>
        <Header>
          <HeaderItemTitle className="w-full font-medium">
            <PageTitle title="CB Tracking" userName={USER.name} />
          </HeaderItemTitle>
        </Header>
        <Body>
          <TrackingSuspense>{renderRoutes(routesUserRender)}</TrackingSuspense>
          <ExportCSVComponent />
          <NotificationSystem />
        </Body>
      </Content>
    </Container>
  );
};

export default React.memo(Layout1);
